class AnimatedFeatureCards extends HTMLElement {
  constructor() {
    super();

    this.container = null;
    this.viewport = null;
    this.slides = [];
    this.dots = [];
    this.playPauseBtn = null;
    this.currentIndex = 0;
    this.interval = null;
    this.autoplaySpeed = 5000;
    this.autoplayEnabled = true;
    this.isPlaying = false;
    this.userPaused = false;
    this.swipePointerId = null;
    this.swipeStartX = 0;
    this.swipeStartY = 0;
    this.suppressClick = false;
    this.desktopMediaQuery = window.matchMedia('(min-width: 750px)');

    this.handleVisibilityChange = this.onVisibilityChange.bind(this);
    this.handleResponsiveChange = this.onResponsiveChange.bind(this);
    this.handlePointerEnter = this.onPointerEnter.bind(this);
    this.handlePointerLeave = this.onPointerLeave.bind(this);
    this.handleSwipeStart = this.onSwipeStart.bind(this);
    this.handleSwipeEnd = this.onSwipeEnd.bind(this);
    this.handleSwipeCancel = this.onSwipeCancel.bind(this);
    this.handleClickCapture = this.onClickCapture.bind(this);
  }

  connectedCallback() {
    this.viewport = this.querySelector(".animated-fc__viewport");
    this.container = this.querySelector(".animated-fc__container");
    if (!this.container) return;

    this.slides = Array.from(this.container.children);
    if (this.slides.length === 0) return;

    this.dots = Array.from(this.querySelectorAll(".animated-fc__dot"));
    this.playPauseBtn = this.querySelector(".animated-fc__btn");
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed || "5000", 10);
    this.autoplayEnabled =
      this.dataset.autoplay !== "false" &&
      this.slides.length > 1 &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    this.slides.forEach((slide, index) => {
      slide.classList.add("animated-fc__slide");
      slide.setAttribute("data-active", index === 0 ? "true" : "false");
      slide.setAttribute("aria-hidden", index === 0 ? "false" : "true");
      this.prepareAnimatedContent(slide);
    });

    this.dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const nextIndex = parseInt(dot.dataset.index || "0", 10);
        this.goToSlide(nextIndex, { userTriggered: true });
      });
    });

    if (this.playPauseBtn) {
      this.playPauseBtn.addEventListener("click", () => {
        if (this.isPlaying) {
          this.userPaused = true;
          this.stopAutoplay();
        } else {
          this.userPaused = false;
          this.startAutoplay();
        }
      });
    }

    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    this.addEventListener("pointerenter", this.handlePointerEnter);
    this.addEventListener("pointerleave", this.handlePointerLeave);
    this.viewport?.addEventListener("pointerdown", this.handleSwipeStart, { passive: true });
    this.viewport?.addEventListener("pointerup", this.handleSwipeEnd);
    this.viewport?.addEventListener("pointercancel", this.handleSwipeCancel);
    this.viewport?.addEventListener("click", this.handleClickCapture, true);
    this.desktopMediaQuery.addEventListener?.('change', this.handleResponsiveChange);

    this.updateDots();
    this.syncResponsiveState();

    if (this.autoplayEnabled && !this.desktopMediaQuery.matches) {
      this.startAutoplay();
    } else {
      this.updateButtonState();
    }
  }

  disconnectedCallback() {
    document.removeEventListener("visibilitychange", this.handleVisibilityChange);
    this.removeEventListener("pointerenter", this.handlePointerEnter);
    this.removeEventListener("pointerleave", this.handlePointerLeave);
    this.viewport?.removeEventListener("pointerdown", this.handleSwipeStart);
    this.viewport?.removeEventListener("pointerup", this.handleSwipeEnd);
    this.viewport?.removeEventListener("pointercancel", this.handleSwipeCancel);
    this.viewport?.removeEventListener("click", this.handleClickCapture, true);
    this.desktopMediaQuery.removeEventListener?.('change', this.handleResponsiveChange);

    this.stopAutoplay({ silent: true });
  }

  prepareAnimatedContent(slide) {
    const animatedNodes = Array.from(
      new Set(
        slide.querySelectorAll(
          [
            ".image-block",
            ".image-block__image",
            ".text-block",
            ".text-block > *",
            ".group-block-content > *",
            "h1",
            "h2",
            "h3",
            "h4",
            "h5",
            "h6",
            "p",
            "li",
            "a",
            ".button",
          ].join(", "),
        ),
      ),
    );

    animatedNodes.forEach((node, index) => {
      node.style.setProperty("--feature-card-delay", `${0.08 + index * 0.08}s`);
    });
  }

  updateDots() {
    this.dots.forEach((dot, index) => {
      dot.setAttribute("aria-current", index === this.currentIndex ? "true" : "false");
    });
  }

  syncResponsiveState() {
    const isDesktop = this.desktopMediaQuery.matches;

    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentIndex;
      slide.setAttribute('data-active', isDesktop || isActive ? 'true' : 'false');
      slide.setAttribute('aria-hidden', isDesktop || isActive ? 'false' : 'true');
    });

    if (isDesktop) {
      this.stopAutoplay({ silent: true });
      this.updateButtonState();
    } else if (this.autoplayEnabled && !this.userPaused && !document.hidden) {
      this.startAutoplay();
    }
  }

  onResponsiveChange() {
    this.syncResponsiveState();
  }

  updateButtonState() {
    if (!this.playPauseBtn) return;

    const canPlay = this.autoplayEnabled;
    this.playPauseBtn.hidden = !canPlay;
    if (!canPlay) return;

    const playing = this.isPlaying && !document.hidden;
    this.playPauseBtn.setAttribute("data-state", playing ? "playing" : "paused");
    this.playPauseBtn.setAttribute(
      "aria-label",
      playing ? "Pause slide rotation" : "Play slide rotation",
    );
  }

  startAutoplay() {
    if (!this.autoplayEnabled || this.desktopMediaQuery.matches) return;

    this.stopAutoplay({ silent: true });
    this.isPlaying = true;
    this.updateButtonState();

    this.interval = window.setInterval(() => {
      this.nextSlide();
    }, this.autoplaySpeed);
  }

  stopAutoplay({ silent = false } = {}) {
    if (this.interval) {
      window.clearInterval(this.interval);
      this.interval = null;
    }

    this.isPlaying = false;
    if (!silent) {
      this.updateButtonState();
    }
  }

  nextSlide() {
    if (this.slides.length <= 1) return;
    this.goToSlide((this.currentIndex + 1) % this.slides.length);
  }

  previousSlide(options = {}) {
    if (this.slides.length <= 1) return;
    this.goToSlide((this.currentIndex - 1 + this.slides.length) % this.slides.length, options);
  }

  goToSlide(index, { userTriggered = false } = {}) {
    if (!this.slides[index] || index === this.currentIndex) return;

    this.slides[this.currentIndex]?.setAttribute("data-active", "false");
    this.slides[this.currentIndex]?.setAttribute("aria-hidden", "true");
    this.currentIndex = index;
    this.slides[this.currentIndex]?.setAttribute("data-active", "true");
    this.slides[this.currentIndex]?.setAttribute("aria-hidden", "false");

    this.updateDots();

    if (userTriggered && this.autoplayEnabled && this.isPlaying) {
      this.startAutoplay();
    }
  }

  onVisibilityChange() {
    if (!this.autoplayEnabled) return;

    if (document.hidden) {
      this.stopAutoplay({ silent: true });
      this.updateButtonState();
    } else if (!this.userPaused) {
      this.startAutoplay();
    }
  }

  onPointerEnter(event) {
    if (event?.pointerType && event.pointerType !== "mouse") return;
    if (!this.autoplayEnabled || !this.isPlaying) return;
    this.stopAutoplay({ silent: true });
    this.updateButtonState();
  }

  onPointerLeave(event) {
    if (event?.pointerType && event.pointerType !== "mouse") return;
    if (!this.autoplayEnabled || document.hidden || this.userPaused) return;
    this.startAutoplay();
  }

  onSwipeStart(event) {
    if (event.pointerType === "mouse" || !event.isPrimary || this.slides.length <= 1) return;

    this.swipePointerId = event.pointerId;
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
    event.currentTarget?.setPointerCapture?.(event.pointerId);
  }

  onSwipeEnd(event) {
    if (this.swipePointerId !== event.pointerId) return;

    const deltaX = event.clientX - this.swipeStartX;
    const deltaY = event.clientY - this.swipeStartY;
    event.currentTarget?.releasePointerCapture?.(event.pointerId);
    this.onSwipeCancel();

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

    this.suppressClick = true;
    window.setTimeout(() => {
      this.suppressClick = false;
    }, 350);

    if (deltaX < 0) {
      this.goToSlide((this.currentIndex + 1) % this.slides.length, { userTriggered: true });
    } else {
      this.previousSlide({ userTriggered: true });
    }
  }

  onSwipeCancel(event) {
    if (event && this.swipePointerId === event.pointerId) {
      event.currentTarget?.releasePointerCapture?.(event.pointerId);
    }

    this.swipePointerId = null;
    this.swipeStartX = 0;
    this.swipeStartY = 0;
  }

  onClickCapture(event) {
    if (!this.suppressClick) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.suppressClick = false;
  }
}

if (!customElements.get("animated-feature-cards")) {
  customElements.define("animated-feature-cards", AnimatedFeatureCards);
}
